<template>
  <div class="invoice-item">
    <div class="info-section">
      <div class="card-header" @click="toggleOpen">
        <div class="header-left">
          <base-toggle-arrow-button
            :section-visible="isOpen"
            @click.stop="toggleOpen"
          />
          <div class="header-title-group">
            <h4 class="card-title">
              Счёт #{{ invoice.number || invoice.id }}
            </h4>
            <span
              class="status-text"
              :style="{
                backgroundColor: invoiceStatusColor(invoice.status),
              }"
            >
              {{ formatInvoiceStatus(invoice.status) }}
            </span>
            <span
              class="positions-badge"
              v-if="invoice.positions_count"
            >
              {{ invoice.positions_count }}
              {{ positionsLabel(invoice.positions_count) }}
            </span>
          </div>
        </div>

        <div class="header-right" @click.stop>
          <span class="amount-total" v-if="invoice.total_amount">
            {{ formatAmount(invoice.total_amount) }}
            {{ invoice.currency || "RUB" }}
          </span>

          <div class="right-actions">
            <base-button
              class="btn"
              :loading="isPrintLoading"
              @click.stop="handlePrint"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                style="vertical-align: middle; margin-right: 5px"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path
                  d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
                />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Напечатать
            </base-button>

            <base-button @click="startEditing">
              Редактировать
            </base-button>
          </div>
        </div>
      </div>

      <div v-if="isEditing" class="card-content">
        <OrderInvoiceForm
          :order-id="orderId"
          :company-id="companyId"
          :initial-data="invoice"
          @save="handleUpdateSuccess"
          @cancel="isEditing = false"
        />
      </div>

      <div v-else-if="isOpen" class="card-content">
        <div class="info-columns">
          <div class="info-column">
            <div class="info-section-inner">
              <div class="card-header-inner">
                <h4 class="card-title">Пользовательские поля</h4>
              </div>
              <div class="card-content-inner">
                <div class="info-item">
                  <span class="info-label">Номер:</span>
                  <span class="view-value">
                    {{ invoice.number || "—" }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">Сумма:</span>
                  <span class="view-value">
                    {{ invoice.amount || "—" }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">Валюта:</span>
                  <span class="view-value">
                    {{ invoice.currency || "—" }}
                  </span>
                </div>
              </div>
            </div>

            <div class="info-section-inner">
              <div class="card-header-inner">
                <h4 class="card-title">Даты</h4>
              </div>
              <div class="card-content-inner">
                <div class="info-item">
                  <span class="info-label">Дата выставления:</span>
                  <span class="view-value">
                    {{
                      invoice.issued_on
                        ? formatDate(invoice.issued_on)
                        : "—"
                    }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">Срок оплаты:</span>
                  <span class="view-value">
                    {{
                      invoice.due_on
                        ? formatDate(invoice.due_on)
                        : "—"
                    }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">Дата оплаты:</span>
                  <span class="view-value">
                    {{
                      invoice.paid_on
                        ? formatDate(invoice.paid_on)
                        : "—"
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="info-column">
            <div class="info-section-inner">
              <div class="card-header-inner">
                <h4 class="card-title">Стороны счёта</h4>
              </div>
              <div class="card-content-inner">
                <div
                  v-if="invoiceCustomer || invoiceContractor"
                  class="entities-list"
                >
                  <div
                    v-if="invoiceCustomer"
                    class="entity-card"
                    @click="goToCounterparty(invoice.customer_id)"
                  >
                    <div class="info-item counterparty-item">
                      <span
                        class="info-label entity-role role-customer"
                      >
                        Заказчик
                      </span>
                      <span class="view-value">
                        {{ invoiceCustomer }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="invoiceContractor"
                    class="entity-card"
                    @click="goToCounterparty(invoice.contractor_id)"
                  >
                    <div class="info-item counterparty-item">
                      <span
                        class="info-label entity-role role-contractor"
                      >
                        Исполнитель
                      </span>
                      <span class="view-value">
                        {{ invoiceContractor }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data">Не указаны</div>
              </div>
            </div>

            <div class="info-section-inner">
              <div class="card-header-inner">
                <h4 class="card-title">Суммы</h4>
              </div>
              <div class="card-content-inner">
                <div class="info-item">
                  <span class="info-label">Сумма без НДС:</span>
                  <span class="view-value">
                    {{
                      invoice.base_amount
                        ? formatAmount(invoice.base_amount)
                        : "—"
                    }}
                    {{
                      invoice.base_amount
                        ? invoice.currency || "RUB"
                        : ""
                    }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">Сумма НДС:</span>
                  <span class="view-value">
                    {{
                      invoice.vat_amount
                        ? formatAmount(invoice.vat_amount)
                        : "—"
                    }}
                    {{
                      invoice.vat_amount
                        ? invoice.currency || "RUB"
                        : ""
                    }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">Итого:</span>
                  <span class="view-value amount-highlight">
                    {{
                      invoice.total_amount
                        ? formatAmount(invoice.total_amount)
                        : "—"
                    }}
                    {{
                      invoice.total_amount
                        ? invoice.currency || "RUB"
                        : ""
                    }}
                  </span>
                </div>
                <div
                  class="info-item"
                  v-if="invoice.total_amount_in_words"
                >
                  <span class="info-label">Прописью:</span>
                  <span class="view-value amount-in-words">
                    {{ invoice.total_amount_in_words }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="items-section">
          <OrderInvoiceItemsList
            :invoice-id="invoice.id"
            :order-id="orderId"
            :company-id="companyId"
            :invoice-totals="invoice"
            @items-updated="$emit('updated')"
          />
        </div>
      </div>
    </div>

    <OrderInvoiceTemplateSelectModal
      :show="isTemplateModalOpen"
      :templates="invoiceTemplates"
      @close="isTemplateModalOpen = false"
      @select="handleTemplateSelect"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";

import { useOrderInvoiceStore } from "@/stores/orderInvoiceStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useCounterpartyStore } from "@/stores/counterpartyStore";
import { useTemplateStore } from "@/stores/templateStore";

import { formatDate } from "@/utils/dateUtils";

import OrderInvoiceForm from "./OrderInvoiceForm.vue";
import OrderInvoiceItemsList from "./OrderInvoiceItemsList.vue";
import OrderInvoiceTemplateSelectModal from "./OrderInvoiceTemplateSelectModal.vue";

export default {
  name: "OrderInvoiceItem",
  components: {
    OrderInvoiceForm,
    OrderInvoiceItemsList,
    OrderInvoiceTemplateSelectModal,
  },
  props: {
    invoice: { type: Object, required: true },
    orderId: { type: [String, Number], required: true },
    companyId: { type: [String, Number], required: true },
  },
  emits: ["updated"],
  data() {
    return {
      isOpen: false,
      isEditing: false,
      isTemplateModalOpen: false,
      isPrintLoading: false,
    };
  },
  computed: {
    ...mapState(useCounterpartyStore, ["counterparties"]),
    ...mapState(useTemplateStore, ["templates"]),

    notificationStore() {
      return useNotificationStore();
    },

    invoiceCustomer() {
      return this.resolveCounterparty(this.invoice.customer_id);
    },

    invoiceContractor() {
      return this.resolveCounterparty(this.invoice.contractor_id);
    },

    invoiceTemplates() {
      return this.templates.filter(
        (t) => t.kind === "order_invoice" && t.enabled === true
      );
    },
  },
  methods: {
    ...mapActions(useOrderInvoiceStore, [
      "formatInvoiceStatus",
      "invoiceStatusColor",
    ]),
    ...mapActions(useCounterpartyStore, ["ensureCounterparty"]),
    ...mapActions(useTemplateStore, [
      "renderTemplate",
      "getTemplatesRequest",
    ]),

    resolveCounterparty(id) {
      if (!id) return null;
      const found = this.counterparties.find(
        (c) => String(c.id) === String(id)
      );
      if (found) {
        return found.inn
          ? `${found.name} - ${found.inn}`
          : found.name;
      }
      return `Контрагент #${id}`;
    },

    goToCounterparty(id) {
      if (!id) return;
      this.$router.push({
        name: "CounterpartyDetails",
        params: { id },
      });
    },

    async toggleOpen() {
      if (!this.isEditing) {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          await Promise.all([
            this.ensureCounterparty(this.invoice.customer_id),
            this.ensureCounterparty(this.invoice.contractor_id),
          ]);
        }
      }
    },

    startEditing() {
      this.isOpen = true;
      this.isEditing = true;
      Promise.all([
        this.ensureCounterparty(this.invoice.customer_id),
        this.ensureCounterparty(this.invoice.contractor_id),
      ]);
    },

    async handleUpdateSuccess() {
      this.isEditing = false;
      this.notificationStore.showSuccess("Счёт обновлён");
      this.$emit("updated");
    },

    async handlePrint() {
      this.isPrintLoading = true;
      try {
        if (!this.templates.length) {
          await this.getTemplatesRequest();
        }

        if (!this.invoiceTemplates.length) {
          this.notificationStore.showError(
            "Нет доступных шаблонов для счёта"
          );
          return;
        }

        if (this.invoiceTemplates.length === 1) {
          await this.renderTemplate(this.invoiceTemplates[0].id, {
            record_id: this.invoice.id,
            record_type: "Order::Invoice",
          });
          return;
        }

        // Несколько шаблонов - открываем модалку выбора
        this.isTemplateModalOpen = true;
      } finally {
        this.isPrintLoading = false;
      }
    },

    async handleTemplateSelect(template) {
      this.isTemplateModalOpen = false;

      await this.renderTemplate(template.id, {
        record_id: this.invoice.id,
        record_type: "Order::Invoice",
      });
    },

    positionsLabel(count) {
      const n = Number(count);
      if (n === 1) return "позиция";
      if (n >= 2 && n <= 4) return "позиции";
      return "позиций";
    },

    formatAmount(value) {
      return parseFloat(value).toLocaleString("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate,
  },
};
</script>

<style scoped>
.info-section {
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  background: #fff;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--base-in-indent);
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  gap: 15px;
}

.card-header:hover {
  background: #e6f5ed;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
  margin: 0;
}

.status-text {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.positions-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  background: var(--color-dark);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: default;
  flex-shrink: 0;
}

.amount-total {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-dark);
  white-space: nowrap;
}

.right-actions {
  display: flex;
  gap: 15px;
}

.card-content {
  padding: var(--base-in-indent);
}

.info-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--base-in-indent);
  margin-bottom: var(--base-in-indent);
}

.info-column {
  display: flex;
  flex-direction: column;
  gap: var(--base-in-indent);
}

.info-section-inner {
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  background: #fff;
}

.card-header-inner {
  padding: var(--base-in-indent);
  background: rgb(240, 240, 240);
  border-bottom: 1px solid #e8e8e8;
}

.card-content-inner {
  padding: var(--base-in-indent);
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 15px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.counterparty-item {
  align-items: center;
  margin-bottom: 0;
}

.info-label {
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
}

.view-value {
  color: var(--color-dark);
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}

.amount-highlight {
  font-weight: 700;
  color: #4f46e5;
  font-size: 15px;
}

.amount-in-words {
  font-size: 13px;
  font-style: italic;
  color: #555;
}

.entities-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.entity-card {
  border-radius: 8px;
  padding: 12px;
  background: rgb(240, 240, 240);
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: all 0.3s ease;
}

.entity-card:hover {
  background: #e6f5ed;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.entity-role {
  display: inline-block;
  padding: 5px 10px;
  font-weight: 600;
  color: #fff !important;
  border-radius: 4px;
  white-space: nowrap;
}

.role-customer {
  background-color: #9333ea;
}

.role-contractor {
  background-color: #0d9488;
}

.items-section {
  margin-top: var(--base-in-indent);
}

@media (max-width: 1200px) {
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .view-value {
    text-align: left;
  }
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    flex-wrap: wrap;
    flex-direction: column;
    gap: var(--base-in-indent);
    align-items: flex-start;
  }

  .info-columns {
    grid-template-columns: 1fr;
  }
}
</style>
